import React, { useEffect, useState, useMemo, memo } from 'react';
import "./styles/Carte.css";
import axios from "./AxiosInstance"; // adapte le chemin si besoin
import Hexagon from "./Hexagon";
import useMediaQuery from "../hooks/useMediaQuery";
import Zoom from "./ZoomableContainer.jsx";


const HexMap = memo(({ countries_dict, ocean, onOpenPopin, gridData: initialGridData, animationPhase = null, playerCountryId = null }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const hexSize = 19;
    const hexWidth = Math.sqrt(3) * hexSize;
    const hexHeight = 2 * hexSize;
    const cols = 30;
    const rows = 15;
    const countryList = Object.keys(countries_dict);
    // Zhongo Eurocorp Pétrolistan Volgovie Autoristan Sudica Neo-Atlas Bricologia
    // "#ff6b6b", "#3b82f6", "#f89c74", "#0f172ae6", "#8b5cf6", "#f59e0b", "white", "#059669"

    const countryColors = [
        "#ff6b6b", // Zhongo Bleu principal
        "#1e40af", // Eurocorp Bleu foncé
        "#833a01", // Pétrolistan Marron
        "#1f2937", // Volgovie Gris foncé
        "#8b5cf6", // Autoristan Indigo
        "#f59e0b", // Sudica Violet
        "white", // Neo-Atlas blanc
        "#059669"  // Bricologia Emeraude
    ];

    const countryNames = [
        "Zhongo",
        "Eurocorp",
        "Petrolistan",
        "Volgovie",
        "Autoristan",
        "Sudica",
        "Neo Atlas",
        "Bricologia"
    ];

    // Transformation en dictionnaire
    const countryColorMap = countryNames.reduce((acc, country, index) => {
        acc[country] = countryColors[index];
        return acc;
    }, {});

    const [internalGridData, setInternalGridData] = useState([]);

    // Utiliser les données passées en props ou celles chargées
    const gridData = initialGridData || internalGridData;

    const getHexPosition = (row, col) => {
        const x = col * hexWidth + (row % 2 === 0 ? 0 : hexWidth / 2) + 25;
        const y = row * (hexHeight * 0.75) + 25;
        return [x, y];
    };

    const getHexPoints = (cx, cy) => {
        return [...Array(6)].map((_, i) => {
            const angle = Math.PI / 180 * (60 * i - 30);
            const x = cx + hexSize * Math.cos(angle);
            const y = cy + hexSize * Math.sin(angle);
            return `${x},${y}`;
        }).join(' ');
    };

    useEffect(() => {
        // Si on a déjà des données via props, on ne charge pas
        if (initialGridData) return;

        const fetchGrid = async () => {
            try {
                const response = await axios.get("/grid");
                setInternalGridData(response.data);
            } catch (error) {
                console.error("Erreur lors du chargement de la grille :", error);
            }
        };

        fetchGrid();
    }, [countries_dict, initialGridData]);

    const getCountryColor = (country) => {
        return countryColorMap[country] || "#4ecdc4";
    };

    const countryCenters = useMemo(() => {
        const centers = {};
        const countries = {};

        for (const cell of gridData) {
            if (!cell.country) continue;
            if (!countries[cell.country]) {
                countries[cell.country] = [];
            }
            countries[cell.country].push({ x: cell.x, y: cell.y });
        }
        // Calculer le centre de chaque pays
        for (const [countryId, cells] of Object.entries(countries)) {
            const sum = cells.reduce((acc, val) => ({
                x: acc.x + val.x,
                y: acc.y + val.y
            }), { x: 0, y: 0 });

            const avgX = sum.x / cells.length;
            const avgY = sum.y / cells.length;

            // Trouve la cellule la plus proche du centre
            let closest = null;
            let minDist = Infinity;

            for (const cell of cells) {
                const dx = cell.x - avgX;
                const dy = cell.y - avgY;
                const dist = dx * dx + dy * dy;

                if (dist < minDist) {
                    minDist = dist;
                    closest = cell;
                }
            }

            centers[countryId] = closest;
        }

        return centers;
    }, [gridData]);


    const generateMap = () => {
        const map = [];

        // En multi, playerCountryId identifie le pays du joueur actuel.
        // En solo, on cherche le premier pays pnj === 0.
        const player = playerCountryId
            ? Object.entries(countries_dict).find(([name, data]) => data.id === playerCountryId)
            : Object.entries(countries_dict).find(([name, data]) => data.pnj === 0);

        if (player) {
            const [countryName, countryData] = player;
            const enemies = countryData.enemies;
            const allies = countryData.allies;

            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    let status = "neutre";

                    const gridItem = gridData.find(item => item.x === col && item.y === row);
                    const countryId = gridItem ? gridItem.country : null;
                    const nouveau = gridItem ? gridItem.new : null;
                    const lose = gridItem ? gridItem.lose : false;
                    const hasResource = gridItem?.resources > 15;
                    const terrain = countryId ? 'land' : 'water';
                    const resources = gridItem ? gridItem.resources : null;
                    const isCity = gridItem ? gridItem.city : null;
                    const biome = gridItem ? gridItem.biome : null;
                    const countryShortName = countryList.find(name => countries_dict[name].id === countryId);
                    if (countryData.id == countryId) {
                        status = "joueur";
                    }
                    if (enemies.includes(countryShortName)) {
                        status = "ennemi";
                    } else if (allies.includes(countryShortName)) {
                        status = "allies";
                    }

                    map.push(
                        <Hexagon
                            key={`${row}-${col}`}
                            row={row}
                            col={col}
                            terrain={terrain}
                            countryId={countryId}
                            nouveau={nouveau}
                            hasResource={hasResource}
                            resources={resources}
                            isCity={isCity}
                            status={status}
                            lose={lose}
                            biome={biome}
                            ocean={ocean}
                            name={countryShortName}
                            getHexPosition={getHexPosition}
                            getHexPoints={getHexPoints}
                            getCountryColor={getCountryColor}
                            countryCenters={countryCenters}
                            countryList={countryList}
                            countries_dict={countries_dict}
                            onOpenPopin={onOpenPopin}
                            animationPhase={animationPhase}
                        />
                    );
                }
            }
        }

        return map;
    };

    return (
        <div className="">
            <div className="overflow-hidden">
                <Zoom>
                    <svg
                        viewBox={isMobile ? "0 0 1516 460" : "0 0 1016 460"}
                        preserveAspectRatio="xMidYMid meet"
                        className={isMobile ? "h-auto select-none" : "w-full h-full"}
                        style={isMobile ? { minHeight: '90vh', width: '300vw' } : {}}
                    >
                        <g>
                            {generateMap()}
                        </g>
                    </svg>
                </Zoom>
            </div>
        </div >
    );
});

export default HexMap;